import { ref, uploadBytesResumable, type UploadTaskSnapshot } from "firebase/storage";
import { storage } from "./firebase";

/* ============================================================
   Subir foto para o Cloud Storage

   Existe como arquivo próprio por um motivo específico: `uploadBytes`
   simples não conta nada enquanto sobe e, quando o bucket não responde,
   o SDK tenta de novo sozinho com espera crescente. O resultado é um
   botão travado em "Enviando…" por um minuto e nenhuma pista do motivo —
   que foi exatamente o que aconteceu no primeiro teste.

   Aqui a subida reporta progresso, tem prazo, e traduz o código de erro
   do Firebase para uma frase que diz o que fazer.
   ============================================================ */

export class FalhaNoUpload extends Error {
  constructor(message: string, readonly codigo: string) {
    super(message);
  }
}

/** O que cada código do Storage significa em português, e o que fazer. */
function explicar(codigo: string): string {
  const mapa: Record<string, string> = {
    "storage/unauthorized":
      "O Storage recusou o envio. As regras de Storage precisam estar publicadas.",
    "storage/unauthenticated":
      "Sua sessão expirou. Saia e entre de novo.",
    "storage/retry-limit-exceeded":
      "A foto não subiu depois de várias tentativas. Costuma ser internet instável — "
      + "mas se acontecer sempre, o Cloud Storage pode não estar ativado no projeto.",
    "storage/quota-exceeded":
      "A cota do Storage estourou.",
    "storage/canceled":
      "Envio cancelado.",
    "storage/unknown":
      "O Storage respondeu com um erro sem detalhe. Quase sempre é o bucket não existir "
      + "ainda — confira se o Cloud Storage foi ativado no console do Firebase.",
  };
  return mapa[codigo] ?? `O Storage recusou (${codigo}).`;
}

export interface Progresso {
  /** 0 a 1 somando todos os arquivos do envio. */
  fracao: number;
  enviados: number;
  total: number;
}

/**
 * Sobe um arquivo, desistindo quando para de andar.
 *
 * O prazo não é do envio inteiro — foto grande em 3G lenta demora e isso
 * é legítimo. O prazo é de INATIVIDADE: se em 20 segundos nenhum byte se
 * move, alguma coisa está errada e é melhor dizer o quê do que continuar
 * fingindo que está subindo.
 *
 * Zero byte transferido é o caso mais informativo dos dois: quando o
 * bucket não existe, o SDK tenta de novo com espera crescente e nunca
 * chega a mandar nada. Foi exatamente esse o "Enviando…" de 60 segundos
 * do primeiro teste.
 */
export function subir(
  caminho: string,
  dados: Blob,
  aoProgredir?: (bytes: number) => void,
  paradoMs = 20_000,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tarefa = uploadBytesResumable(ref(storage, caminho), dados, {
      cacheControl: "public, max-age=31536000, immutable",
    });

    let andou = 0;
    let relogio: ReturnType<typeof setTimeout>;

    const desistir = () => {
      tarefa.cancel();
      reject(new FalhaNoUpload(
        andou
          ? "O envio da foto parou no meio e não voltou. Confira a internet e tente de novo."
          : "A foto não começou a subir. Quase sempre é o Cloud Storage não estar "
            + "ativado no projeto, ou o nome do bucket estar errado na configuração.",
        andou ? "parou-no-meio" : "nunca-comecou",
      ));
    };

    const rearmar = () => {
      clearTimeout(relogio);
      relogio = setTimeout(desistir, paradoMs);
    };
    rearmar();

    tarefa.on(
      "state_changed",
      (snap: UploadTaskSnapshot) => {
        if (snap.bytesTransferred > andou) {
          andou = snap.bytesTransferred;
          rearmar();
        }
        aoProgredir?.(snap.bytesTransferred);
      },
      (err) => {
        clearTimeout(relogio);
        const codigo = (err as { code?: string }).code ?? "storage/unknown";
        // `cancel()` acima também cai aqui; a mensagem boa já foi rejeitada.
        if (codigo !== "storage/canceled") {
          reject(new FalhaNoUpload(explicar(codigo), codigo));
        }
      },
      () => {
        clearTimeout(relogio);
        resolve();
      },
    );
  });
}

/** Sobe vários arquivos em paralelo, com progresso somado. */
export async function subirTodos(
  arquivos: { caminho: string; dados: Blob }[],
  aoProgredir?: (p: Progresso) => void,
): Promise<void> {
  const total = arquivos.reduce((s, a) => s + a.dados.size, 0);
  const feito = new Array<number>(arquivos.length).fill(0);

  const avisar = () => {
    const enviados = feito.reduce((a, b) => a + b, 0);
    aoProgredir?.({ fracao: total ? enviados / total : 0, enviados, total });
  };

  await Promise.all(arquivos.map((a, i) =>
    subir(a.caminho, a.dados, (bytes) => { feito[i] = bytes; avisar(); })));
}
